import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import React from "react";
import googleLogo from "../../img/google-logo.png";
import office365Logo from "../../img/office-365-logo.png";

interface InboxTypeSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectOffice365: () => void;
  onSelectImap: () => void;
  onSelectGmail: () => void;
}

const InboxTypeSelectionDialog: React.FC<InboxTypeSelectionDialogProps> = ({
  open,
  onClose,
  onSelectOffice365,
  onSelectImap,
  onSelectGmail,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Connect Mailbox
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Choose how you'd like to connect your mailbox:
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
              onClick={onSelectGmail}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <img src={googleLogo} alt="Gmail" style={{ width: 48, height: 48 }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Gmail
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect your Gmail account using secure
                  Google OAuth authentication.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" display="block">
                    • Secure Google OAuth
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Personal & Workspace accounts
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Full email management
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button variant="contained" color="primary">
                  Connect Gmail
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
              onClick={onSelectOffice365}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <img src={office365Logo} alt="Office 365" style={{ width: 48, height: 48 }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Office 365
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect your Microsoft Office 365 or Outlook account using
                  secure OAuth authentication.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" display="block">
                    • Secure Microsoft OAuth
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Office 365 & Outlook
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Full email management
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button variant="contained" color="primary">
                  Connect Office 365
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
              onClick={onSelectImap}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  IMAP Email
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect any email provider using IMAP
                  credentials (Yahoo, custom domains, etc.)
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" display="block">
                    • Any IMAP provider
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Yahoo, custom domains, etc.
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Direct IMAP/SMTP access
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button variant="contained" color="primary">
                  Connect IMAP Email
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InboxTypeSelectionDialog;
